import React from 'react';
import { motion } from 'framer-motion';
import ReviewWorkflowActions from './ReviewWorkflowActions';
import {
  FileText,
  Calendar,
  CheckCircle,
  Clock,
  Archive,
  Eye,
  Lock,
  AlertCircle,
  Globe,
  Smartphone,
  Monitor,
  Tablet,
  BookOpen,
  Building,
  Download,
  Settings,
  Trash2
} from 'lucide-react';

/**
 * Clean Assessment Card with Dropdown Variant Selector
 * Shows only primary assessments in grid, variants accessible via dropdown
 */
const CleanAssessmentCard = ({
  assessment,
  onEdit,
  onImport,
  onManage,
  onManageVPG,
  onPreview,
  onDelete,
  formatDate,
  viewMode = 'grid',
  isVPGLibrary = false,
  isSuperuser = false,
  onReviewChanged,
  isApprover = false,
  onReview
}) => {
  // Logged-in user id, used to scope review-workflow actions (submitter-only).
  let currentUserId = null;
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    currentUserId = u?.id ?? u?.user_id ?? u?.pk ?? null;
  } catch {
    currentUserId = null;
  }

  // Debug logging for VPG cards
  if (isVPGLibrary) {
    console.log('VPG Card Props:', {
      assessmentName: assessment.name,
      isVPGLibrary,
      isSuperuser,
      hasOnPreview: !!onPreview,
      hasOnEdit: !!onEdit,
      hasOnDelete: !!onDelete
    });
  }

  // Check if this is a primary assessment with variants
  const isPrimary = assessment.variants && assessment.variants.length > 0;
  const variantCount = assessment.variants?.length || 0;

  // Status configuration
  const getStatusConfig = (status) => {
    const configs = {
      draft: {
        icon: Clock,
        color: 'amber',
        bgClass: 'bg-amber-50',
        textClass: 'text-amber-700',
        borderClass: 'border-amber-200',
        iconClass: 'text-amber-500',
        label: 'Draft'
      },
      review: {
        icon: Eye,
        color: 'blue',
        bgClass: 'bg-blue-50',
        textClass: 'text-blue-700',
        borderClass: 'border-blue-200',
        iconClass: 'text-blue-500',
        label: 'Pending Review'
      },
      in_review: {
        icon: Eye,
        color: 'blue',
        bgClass: 'bg-blue-50',
        textClass: 'text-blue-700',
        borderClass: 'border-blue-200',
        iconClass: 'text-blue-500',
        label: 'Pending Review'
      },
      approved: {
        icon: CheckCircle,
        color: 'teal',
        bgClass: 'bg-teal-50',
        textClass: 'text-teal-700',
        borderClass: 'border-teal-200',
        iconClass: 'text-teal-500',
        label: 'Approved'
      },
      denied: {
        icon: AlertCircle,
        color: 'red',
        bgClass: 'bg-red-50',
        textClass: 'text-red-700',
        borderClass: 'border-red-200',
        iconClass: 'text-red-500',
        label: 'Changes Requested'
      },
      published: {
        icon: CheckCircle,
        color: 'emerald',
        bgClass: 'bg-emerald-50',
        textClass: 'text-emerald-700',
        borderClass: 'border-emerald-200',
        iconClass: 'text-emerald-500',
        label: 'Published'
      },
      archived: {
        icon: Archive,
        color: 'gray',
        bgClass: 'bg-gray-50',
        textClass: 'text-gray-700',
        borderClass: 'border-gray-200',
        iconClass: 'text-gray-500',
        label: 'Archived'
      }
    };
    return configs[status] || configs.draft;
  };

  const statusConfig = getStatusConfig(assessment.publication_status || assessment.survey?.status || assessment.status);
  const StatusIcon = statusConfig.icon;

  // Locked = submitted and not yet recalled/denied — editing is read-only (FECOA-1614)
  const isLocked = ['in_review', 'approved'].includes(assessment.publication_status);

  // FECOA-1302/1590: another user holds the exclusive edit lock. Show the
  // "currently being edited by X" banner and drop this viewer into View Only.
  const editLock =
    assessment.edit_lock && !assessment.edit_lock.is_self ? assessment.edit_lock : null;
  const formatLockTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch {
      return '';
    }
  };
  const lockBanner = editLock ? (
    <div className="flex items-center gap-1.5 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-xs font-medium text-amber-800 dark:text-amber-300">
      <Lock className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="truncate">
        Currently being edited by <strong>{editLock.locked_by_name}</strong>
        {editLock.acquired_at ? ` (since ${formatLockTime(editLock.acquired_at)})` : ''}
      </span>
    </div>
  ) : null;

  // Library type badge configuration
  const getLibraryConfig = (libraryType) => {
    const configs = {
      vpg: {
        bgClass: 'bg-purple-50',
        textClass: 'text-purple-700',
        borderClass: 'border-purple-200',
        icon: Globe,
        label: 'VPG'
      },
      sponsor: {
        bgClass: 'bg-blue-50',
        textClass: 'text-blue-700',
        borderClass: 'border-blue-200',
        icon: Building,
        label: 'Sponsor'
      },
      study: {
        bgClass: 'bg-primary-50',
        textClass: 'text-primary-700',
        borderClass: 'border-primary-200',
        icon: BookOpen,
        label: 'Study'
      }
    };
    return configs[libraryType];
  };

  const libraryConfig = getLibraryConfig(assessment.library_type);
  const LibraryIcon = libraryConfig?.icon;

  // FECOA-1526: respondent type (COA type) badge — PRO / ObsRO / ClinRO
  const respondentType =
    assessment.respondent_type ||
    assessment.survey?.respondent_type ||
    assessment.survey_detail?.respondent_type;
  const respondentBadgeLabel = {
    participant: 'PRO',
    caregiver: 'ObsRO',
    clinician: 'ClinRO',
  }[respondentType];
  const respondentBadge = respondentBadgeLabel ? (
    <div className="text-xs font-medium px-2.5 py-1 rounded-full border bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700">
      {respondentBadgeLabel}
    </div>
  ) : null;

  const handleCardClick = () => {
    if (isVPGLibrary) {
      // For VPG library: superusers see management modal, others see import modal
      if (isSuperuser && onManageVPG) {
        onManageVPG(assessment);
      } else {
        onImport && onImport(assessment);
      }
    } else if (onManage) {
      // Clicking a card opens its details; editing is an explicit action from
      // there (and review-locked assessments open read-only either way).
      onManage(assessment);
    } else {
      // Fallback for any card rendered without a details handler.
      onEdit && onEdit(assessment);
    }
  };

  // Grid View
  if (viewMode === 'grid') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`bg-white dark:bg-gray-800 rounded-xl border overflow-hidden transition-all duration-200 hover:shadow-lg dark:shadow-gray-900/20 cursor-pointer group relative ${
          editLock ? 'border-amber-300 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700'
        }`}
        onClick={handleCardClick}
      >
        {lockBanner}
        <div className="p-5">
          {/* Header with icon, status, and variant selector */}
          <div className="flex items-start justify-between mb-3">
            <div className="bg-primary-50 p-2 rounded-full">
              <FileText className="w-5 h-5 text-primary-600" />
            </div>
            
            <div className="flex items-center gap-1.5">
              {/* Library badge */}
              {libraryConfig && (
                <div className={`text-xs font-medium px-2.5 py-1 rounded-full border flex items-center space-x-1 ${libraryConfig.bgClass} ${libraryConfig.textClass} ${libraryConfig.borderClass}`}>
                  <LibraryIcon className="w-3.5 h-3.5" />
                  <span>{libraryConfig.label}</span>
                </div>
              )}
              
              {/* Status badge */}
              <div className={`text-xs font-medium px-2.5 py-1 rounded-full border flex items-center space-x-1 ${statusConfig.bgClass} ${statusConfig.textClass} ${statusConfig.borderClass}`}>
                <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.iconClass}`} />
                <span>{statusConfig.label}</span>
              </div>

              {/* Respondent (COA) type badge */}
              {respondentBadge}
            </div>
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors mb-2.5 line-clamp-2">
            {assessment.name || assessment.survey?.name || 'Untitled Assessment'}
          </h3>

          {/* Metadata row - version, type, variants, and date combined */}
          <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-gray-700">v{assessment.version || '1.0'}</span>
              <span>•</span>
              <span>{assessment.assessment_type === 'fixed_form' ? 'Fixed Form' : 'Item Library'}</span>
              {isPrimary && variantCount > 0 && (
                <>
                  <span>•</span>
                  <span className="text-primary-600 font-medium">{variantCount} variant{variantCount !== 1 ? 's' : ''}</span>
                </>
              )}
            </div>
            <div className="flex items-center flex-shrink-0 ml-2">
              <Calendar className="w-3.5 h-3.5 mr-1" />
              <span className="whitespace-nowrap">{formatDate(assessment.builder_metadata?.last_saved || assessment.modified)}</span>
            </div>
          </div>

          {/* Actions footer — wraps so the action cluster never clips on
              narrow cards (e.g. Locked + Awaiting review + Recall + History + Details) */}
          <div className="flex flex-wrap items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700 gap-x-2 gap-y-2">
            {isVPGLibrary ? (
              /* VPG Library: Show preview and copy text, plus superuser controls */
              <React.Fragment>
                <span className="text-sm font-medium text-primary-600">Copy to Library</span>
                <div className="flex items-center gap-1.5">
                  {isSuperuser && onEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(assessment);
                      }}
                      className="px-2 py-1.5 text-xs font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors flex items-center gap-1"
                      title="Edit (Superuser)"
                    >
                      <Settings className="w-3.5 h-3.5" />
                      <span>Edit</span>
                    </button>
                  )}
                  {assessment?.can_delete && onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(assessment);
                      }}
                      className="px-2 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors flex items-center gap-1"
                      title="Archive"
                    >
                      <Archive className="w-3.5 h-3.5" />
                      <span>Archive</span>
                    </button>
                  )}
                  {onPreview && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreview(assessment);
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors flex items-center gap-1.5"
                      title="View assessment details"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>Preview</span>
                    </button>
                  )}
                </div>
              </React.Fragment>
            ) : (
              /* Non-VPG: Show edit/view action */
              <>
                {isLocked ? (
                  <span className="text-sm font-medium text-gray-400 flex items-center gap-1.5" title="Locked while under review">
                    <Lock className="w-3.5 h-3.5" />
                    Locked
                  </span>
                ) : editLock ? (
                  <span className="text-sm font-medium text-amber-600 flex items-center gap-1.5" title={`Being edited by ${editLock.locked_by_name}`}>
                    <Eye className="w-3.5 h-3.5" />
                    View Only
                  </span>
                ) : (
                  <span className="text-sm font-medium text-primary-600">
                    View Details
                  </span>
                )}
                <div className="flex flex-wrap items-center justify-end gap-1.5 min-w-0 ml-auto">
                  <ReviewWorkflowActions
                    entity={assessment}
                    currentUserId={currentUserId}
                    isApprover={isApprover}
                    onReview={onReview}
                    onChanged={onReviewChanged}
                    stopPropagation
                  />
                  {onManage && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onManage(assessment);
                      }}
                      className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors rounded-lg flex items-center gap-1.5"
                      title="View Details"
                    >
                      <Settings className="w-4 h-4" />
                      <span className="text-xs font-medium">Details</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // List View
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white dark:bg-gray-800 rounded-lg border overflow-hidden transition-all duration-200 hover:shadow-lg dark:shadow-gray-900/20 cursor-pointer group ${
        editLock ? 'border-amber-300 dark:border-amber-700' : 'border-gray-200 dark:border-gray-700'
      }`}
      onClick={handleCardClick}
    >
      {lockBanner}
      <div className="p-4 flex items-center justify-between">
        {/* Left section: Icon, Name, and Metadata */}
        <div className="flex items-center flex-1 min-w-0 gap-4">
          <div className="bg-primary-50 p-2.5 rounded-full flex-shrink-0">
            <FileText className="w-5 h-5 text-primary-600" />
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-gray-900 group-hover:text-primary-600 transition-colors truncate mb-1">
              {assessment.name || assessment.survey?.name || 'Untitled Assessment'}
            </h3>

            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="font-medium">v{assessment.version || '1.0'}</span>
              <span>•</span>
              <span>{assessment.assessment_type === 'fixed_form' ? 'Fixed Form' : 'Item Library'}</span>
              {isPrimary && variantCount > 0 && (
                <>
                  <span>•</span>
                  <span className="text-primary-600 font-medium">{variantCount} variant{variantCount !== 1 ? 's' : ''}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right section: Library, Status, Date, Manage button */}
        <div className="flex items-center gap-4 flex-shrink-0 ml-4">
          {/* Library badge */}
          {libraryConfig && (
            <div className={`text-xs font-medium px-2.5 py-1 rounded-full border flex items-center space-x-1 ${libraryConfig.bgClass} ${libraryConfig.textClass} ${libraryConfig.borderClass}`}>
              <LibraryIcon className="w-3.5 h-3.5" />
              <span>{libraryConfig.label}</span>
            </div>
          )}
          
          {/* Status badge */}
          <div className={`text-xs font-medium px-2.5 py-1 rounded-full border flex items-center space-x-1 ${statusConfig.bgClass} ${statusConfig.textClass} ${statusConfig.borderClass}`}>
            <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.iconClass}`} />
            <span>{statusConfig.label}</span>
          </div>

          {/* Respondent (COA) type badge */}
          {respondentBadge}

          {/* Last updated */}
          <div className="text-xs text-gray-500 flex items-center min-w-[110px]">
            <Calendar className="w-3.5 h-3.5 mr-1.5" />
            <span>{formatDate(assessment.builder_metadata?.last_saved || assessment.modified)}</span>
          </div>

          {/* Action buttons */}
          {isVPGLibrary ? (
            /* Preview and superuser buttons for VPG */
            <div className="flex items-center gap-1.5">
              {isSuperuser && onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(assessment);
                  }}
                  className="px-2 py-1.5 text-xs font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-md transition-colors flex items-center gap-1"
                  title="Edit (Superuser)"
                >
                  <Settings className="w-3.5 h-3.5" />
                  <span>Edit</span>
                </button>
              )}
              {assessment?.can_delete && onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(assessment);
                  }}
                  className="px-2 py-1.5 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors flex items-center gap-1"
                  title="Archive"
                >
                  <Archive className="w-3.5 h-3.5" />
                  <span>Archive</span>
                </button>
              )}
              {onPreview && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview(assessment);
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors flex items-center gap-1.5"
                  title="View assessment details"
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span>Preview</span>
                </button>
              )}
            </div>
          ) : (
            /* Review actions + Details button for non-VPG */
            <div className="flex items-center gap-1.5">
              <ReviewWorkflowActions
                entity={assessment}
                currentUserId={currentUserId}
                isApprover={isApprover}
                onReview={onReview}
                onChanged={onReviewChanged}
                stopPropagation
              />
              {onManage && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onManage(assessment);
                  }}
                  className="px-3 py-1.5 text-gray-600 hover:text-primary-600 hover:bg-primary-50 transition-colors rounded-lg flex items-center gap-1.5"
                  title="View Details"
                >
                  <Settings className="w-4 h-4" />
                  <span className="text-xs font-medium">Details</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default CleanAssessmentCard;
